import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import type { SolanaWalletInfo } from '@/providers/TurnkeyProvider';

interface ImportTabProps {
  importWallet: (() => Promise<string>) | undefined;
  allSolanaWallets: SolanaWalletInfo[];
  setActiveWallet: (index: number) => void;
  onPreAction?: () => Promise<void>;
}

export const ImportTab = ({
  importWallet,
  allSolanaWallets,
  setActiveWallet,
  onPreAction,
}: ImportTabProps) => {
  const [isImporting, setIsImporting] = useState(false);

  const handleImportWallet = async () => {
    if (!importWallet) {
      toast.error('Import not available');
      return;
    }

    setIsImporting(true);

    // Call pre-action callback (WalletModal uses this to close modal before Turnkey modal appears)
    if (onPreAction) {
      await onPreAction();
    }

    try {
      const walletId = await importWallet();

      // Auto-select the newly imported wallet
      // The wallet list should have been refreshed by importWallet
      // Find the new wallet by walletId and switch to it
      setTimeout(() => {
        const newWalletIndex = allSolanaWallets.findIndex(w => w.walletId === walletId);
        if (newWalletIndex >= 0) {
          setActiveWallet(newWalletIndex);
        } else {
          // If we can't find by walletId, select the last wallet (most recently added)
          if (allSolanaWallets.length > 0) {
            setActiveWallet(allSolanaWallets.length - 1);
          }
        }
      }, 500); // Small delay to allow wallets to refresh

      toast.success('Wallet imported successfully!');
    } catch (error) {
      console.error('[ImportTab] Import error:', error);
      if (error instanceof Error && !error.message.toLowerCase().includes('cancel')) {
        toast.error(error.message || 'Import failed');
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <Download className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Import Existing Wallet</p>
            <p className="text-xs text-foreground/80 font-normal">
              Import a wallet using your private key from Phantom, Solflare, or any other Solana wallet.
              Your key is securely encrypted and never leaves your device.
            </p>
          </div>
        </div>
      </div>

      <Button
        className="w-full"
        onClick={handleImportWallet}
        disabled={isImporting || !importWallet}
      >
        {isImporting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Importing...
          </>
        ) : (
          <>
            <Download className="h-4 w-4 mr-2" />
            Import Private Key
          </>
        )}
      </Button>

      <p className="text-[11px] text-center text-foreground/70 font-normal">
        Supports Solana private keys in base58 format
      </p>
    </div>
  );
};
